import React from 'react';
import { searchIrlRequests } from '../../../apiServices/irlScreenApi';
import styles from '../irlObservations2026.module.css';
import { ModalShell, WorkstreamOptions } from '../chrome';
import { stripHtmlText } from './shared';

function relatedIrlLabel(item, separator = ' ') {
    const title = stripHtmlText(item?.title || '');
    if (!item?.id) return title;
    return title ? `${item.id}${separator}${title}` : String(item.id);
}

export default function Obs_New_Obs({ draft, onChange, onCreate, onCancel, workstreams, members, editing, busy }) {
    const [relatedHits, setRelatedHits] = React.useState([]);
    const skipSearchRef = React.useRef(Boolean(draft.related));
    const update = (key, value) => onChange({ ...draft, [key]: value });

    React.useEffect(() => {
        const query = (draft.relatedQuery || '').trim();
        if (!query) {
            setRelatedHits([]);
            return undefined;
        }
        if (skipSearchRef.current) {
            skipSearchRef.current = false;
            return undefined;
        }
        const handle = window.setTimeout(() => {
            searchIrlRequests(query)
                .then(data => setRelatedHits(data.results || []))
                .catch(() => setRelatedHits([]));
        }, 250);
        return () => window.clearTimeout(handle);
    }, [draft.relatedQuery]);

    const selectRelated = item => {
        skipSearchRef.current = true;
        setRelatedHits([]);
        onChange({
            ...draft,
            related: item.id,
            relatedQuery: relatedIrlLabel(item),
        });
    };

    const onRelatedQueryChange = event => {
        const relatedQuery = event.target.value;
        const selectedId = String(draft.related || '').trim();
        const keepsSelection =
            Boolean(selectedId) &&
            (relatedQuery === selectedId || relatedQuery.startsWith(`${selectedId} `));
        onChange({
            ...draft,
            relatedQuery,
            related: keepsSelection ? selectedId : '',
        });
    };

    return (
        <ModalShell width="form">
            <div className={styles.formHead}>
                <h2 className={styles.modalTitle}>{editing ? 'Edit observation' : 'New observation'}</h2>
            </div>
            <div className={styles.formBody}>
                <label className={styles.fieldLabel}>Title *</label>
                <input className={styles.field} value={draft.title} onChange={event => update('title', event.target.value)} placeholder="Short title for this observation" />
                <div className={styles.fieldBlock}>
                    <label className={styles.fieldLabel}>Observation *</label>
                    <textarea className={styles.textarea} value={draft.body} onChange={event => update('body', event.target.value)} placeholder="Describe the observation..." />
                </div>
                <div className={styles.fieldBlock}>
                    <label className={styles.fieldLabel}>Why it matters</label>
                    <input className={styles.field} value={draft.why} onChange={event => update('why', event.target.value)} placeholder="Optional — why is this material?" />
                </div>
                <div className={`${styles.fieldRow} ${styles.fieldRow3}`}>
                    <div>
                        <label className={styles.fieldLabel}>Owning workstream *</label>
                        <select className={styles.selectField} value={draft.workstream} onChange={event => update('workstream', event.target.value)}>
                            <option value="">Select a workstream</option>
                            <WorkstreamOptions workstreams={workstreams} valueKey="id" />
                        </select>
                    </div>
                    <div>
                        <label className={styles.fieldLabel}>Materiality</label>
                        <select className={styles.selectField} value={draft.materiality} onChange={event => update('materiality', event.target.value)}>
                            <option>High</option>
                            <option>Medium</option>
                            <option>Low</option>
                        </select>
                    </div>
                    <div>
                        <label className={styles.fieldLabel}>RAG</label>
                        <select className={styles.selectField} value={draft.rag || ''} onChange={event => update('rag', event.target.value)}>
                            <option value="">Select RAG</option>
                            <option value="Red">Red</option>
                            <option value="Amber">Amber</option>
                            <option value="Green">Green</option>
                        </select>
                    </div>
                </div>
                <div className={styles.fieldRow}>
                    <div>
                        <label className={styles.fieldLabel}>Owner</label>
                        <select className={styles.selectField} value={draft.owner} onChange={event => update('owner', event.target.value)}>
                            <option value="">Me</option>
                            {(members || []).map(member => (
                                <option key={member.id} value={member.id}>
                                    {member.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={styles.fieldLabel}>Related IRL</label>
                        <div className={styles.typeahead}>
                            <input
                                className={styles.field}
                                value={draft.relatedQuery != null ? draft.relatedQuery : draft.related || ''}
                                onChange={onRelatedQueryChange}
                                placeholder="Search by IRL id or title..."
                                autoComplete="off"
                            />
                            {relatedHits.length > 0 && (
                                <div className={styles.typeaheadMenu} role="listbox">
                                    {relatedHits.map(item => (
                                        <button key={item.id || item.pk} type="button" onClick={() => selectRelated(item)}>
                                            {relatedIrlLabel(item, ' — ')}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className={styles.formFoot}>
                <button type="button" className={styles.primaryBtn} onClick={() => onCreate('Confirmed')} disabled={!draft.title.trim() || busy}>
                    Confirm Observation
                </button>
                <button type="button" className={styles.outlineBtn} onClick={() => onCreate('Draft')} disabled={!draft.title.trim() || busy}>
                    Save draft
                </button>
                <button type="button" className={styles.mutedBtn} onClick={onCancel} disabled={busy}>
                    Cancel
                </button>
            </div>
        </ModalShell>
    );
}
